import { useEffect, useRef, useState } from 'react';

export interface CommandLineProps {
  mode: 'dj' | 'code';
  busy: boolean;
  placeholder?: string;
  onSend(text: string): Promise<{ ok: boolean; restore?: string } | void> | void;
  onCommand?(cmd: 'code' | 'settings' | 'cc' | 'driving-on' | 'driving-off' | 'reading-on' | 'reading-off'): void;
  onTogglePlay?(): void;
  onPrev?(): void;
  onNext?(): void;
  onVolumeDelta?(d: number): void;
  onEscape?(): void;
  onOpenChange?(open: boolean): void;
  zIndexClass?: string;
}

export function CommandLine(props: CommandLineProps) {
  const { mode, busy } = props;
  const placeholder = props.placeholder ?? (mode === 'dj' ? '说点什么…' : '让 AI 写代码…');
  const z = props.zIndexClass ?? 'z-40';

  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const openRef = useRef(open);
  openRef.current = open;
  const propsRef = useRef(props);
  propsRef.current = props;

  useEffect(() => {
    if (open) requestAnimationFrame(() => inputRef.current?.focus());
  }, [open]);

  useEffect(() => {
    propsRef.current.onOpenChange?.(open);
  }, [open]);

  const openInput = () => {
    setOpen(true);
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  const [takeoff, setTakeoff] = useState(false);
  const openViaBird = () => {
    if (takeoff || openRef.current) return;
    if (mode !== 'dj' || matchMedia('(prefers-reduced-motion: reduce)').matches) return openInput();
    setTakeoff(true);
    setTimeout(() => {
      setTakeoff(false);
      openInput();
    }, 420);
  };

  // 空置自动收起：无内容且 8s 无动作时归位为印章
  useEffect(() => {
    if (!open || text.trim()) return;
    const t = setTimeout(() => {
      setOpen(false);
      setText('');
    }, 8000);
    return () => clearTimeout(t);
  }, [open, text]);

  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.isComposing || e.keyCode === 229) return;
      const el = document.activeElement;
      const tag = el?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || (el as HTMLElement | null)?.isContentEditable) {
        return;
      }
      const p = propsRef.current;
      if (!openRef.current) {
        if (e.key === ' ') {
          e.preventDefault();
          p.onTogglePlay?.();
        } else if (e.key === 'ArrowLeft') {
          p.onPrev?.();
        } else if (e.key === 'ArrowRight') {
          p.onNext?.();
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          p.onVolumeDelta?.(0.05);
        } else if (e.key === 'ArrowDown') {
          e.preventDefault();
          p.onVolumeDelta?.(-0.05);
        } else if (e.key === 'Escape') {
          if (!e.defaultPrevented) p.onEscape?.();
        } else if (e.key.length === 1) {
          e.preventDefault();
          setOpen(true);
          setText((t) => t + e.key);
        }
      } else {
        if (e.key === 'Escape') {
          setOpen(false);
          setText('');
        }
      }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, []);

  const submit = async () => {
    const t = text.trim();
    if (!t) {
      setOpen(false);
      return;
    }
    if (mode === 'dj' && t === '/code') {
      setText('');
      setOpen(false);
      propsRef.current.onCommand?.('code');
      return;
    }
    if (mode === 'dj' && t.toLowerCase() === 'settings') {
      setText('');
      setOpen(false);
      propsRef.current.onCommand?.('settings');
      return;
    }
    if (mode === 'dj' && t.toLowerCase() === 'cc') {
      setText('');
      setOpen(false);
      propsRef.current.onCommand?.('cc');
      return;
    }
    if (mode === 'dj' && (t === '开车' || t.toLowerCase() === 'drive')) {
      setText('');
      setOpen(false);
      propsRef.current.onCommand?.('driving-on');
      return;
    }
    if (mode === 'dj' && (t === '到了' || t === '停车' || t.toLowerCase() === 'park')) {
      setText('');
      setOpen(false);
      propsRef.current.onCommand?.('driving-off');
      return;
    }
    if (mode === 'dj' && (t === '读书' || t === '看书' || t.toLowerCase() === 'read')) {
      setText('');
      setOpen(false);
      propsRef.current.onCommand?.('reading-on');
      return;
    }
    if (mode === 'dj' && (t === '读完了' || t === '不读了')) {
      setText('');
      setOpen(false);
      propsRef.current.onCommand?.('reading-off');
      return;
    }
    if (busy) return;
    setText('');
    const r = await propsRef.current.onSend(t);
    if (r && r.ok === false) {
      setText(r.restore ?? t);
      setOpen(true);
      inputRef.current?.focus();
      return;
    }
    setOpen(false);
  };

  const ik = (e: React.KeyboardEvent<HTMLInputElement>) => {
    e.stopPropagation();
    if (e.key === 'Enter') {
      if (e.nativeEvent.isComposing || e.keyCode === 229) return;
      submit();
    } else if (e.key === 'Escape') {
      setText('');
      setOpen(false);
      (e.target as HTMLInputElement).blur();
    }
  };

  return (
    <>
      <div
        className={`fixed inset-x-0 bottom-0 ${z} ${
          open ? '' : 'pointer-events-none h-0 overflow-hidden opacity-0'
        }`}
      >
        <div className="mx-auto max-w-xl px-6 pb-[max(1rem,env(safe-area-inset-bottom))]">
          <div className="flex items-baseline gap-2">
            <span className="shrink-0 text-seal">▏</span>
            <input
              ref={inputRef}
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={ik}
              placeholder={placeholder}
              className="font-display w-full border-0 border-b bd bg-transparent py-2 text-base text-fg outline-none placeholder-faint focus:border-fg/40"
              style={{ borderBottomWidth: 1 }}
              autoCapitalize="off"
              autoCorrect="off"
            />
            {busy && <span className="shrink-0 text-xs text-faint">…</span>}
          </div>
        </div>
      </div>
      {!open && (
        <button
          aria-label={mode === 'dj' ? '和 Claudio 说话' : '输入指令'}
          title={mode === 'dj' ? '和 Claudio 说话（敲任意键也行）' : '输入指令'}
          onClick={openViaBird}
          onPointerEnter={(e) => {
            if (e.pointerType === 'mouse') openViaBird();
          }}
          className={`fixed bottom-0 left-1/2 ${z} -translate-x-1/2 px-6 pb-[max(0.9rem,env(safe-area-inset-bottom))] pt-3`}
        >
          {mode === 'dj' ? (
            <img
              src="/assets/qingniao.png"
              alt=""
              className={`msg-bird${takeoff ? ' takeoff' : ''}`}
              draggable={false}
            />
          ) : (
            <span className="seal-chip">码</span>
          )}
        </button>
      )}
    </>
  );
}
